import { MAX_CARRIED_SLOTS } from './constants';
import { DRAW_SKILL_ID } from './essence-abilities';
import { ACTION_BAR_HOTKEY_COUNT, DRAW_ACTION_BAR_INDEX } from './types/action-bar';
import type { ActionBarHotkeyTarget } from './types/action-bar';

/**
 * A new character's hotkeys: Draw in the last slot, the rest unassigned. Draw
 * is how a character recovers essence in a run, so every character has it
 * from the start.
 */
export function createDefaultActionBarHotkeys(): ActionBarHotkeyTarget[] {
    return Array.from({ length: ACTION_BAR_HOTKEY_COUNT }, (_, index) => (
        index === DRAW_ACTION_BAR_INDEX ? { kind: 'skill', skillId: DRAW_SKILL_ID } : null
    ));
}

function parseHotkeyTarget(value: unknown): ActionBarHotkeyTarget | undefined {
    if (value === null) {
        return null;
    }
    if (typeof value !== 'object') {
        return undefined;
    }

    const candidate = value as { kind?: unknown; slotIndex?: unknown; skillId?: unknown };
    if (
        candidate.kind === 'carried-slot'
        && typeof candidate.slotIndex === 'number'
        && Number.isInteger(candidate.slotIndex)
        && candidate.slotIndex >= 0
        && candidate.slotIndex < MAX_CARRIED_SLOTS
    ) {
        return { kind: 'carried-slot', slotIndex: candidate.slotIndex };
    }
    if (candidate.kind === 'skill' && typeof candidate.skillId === 'string' && candidate.skillId.trim().length > 0) {
        return { kind: 'skill', skillId: candidate.skillId };
    }
    return undefined;
}

/**
 * Validates an action-bar hotkey mapping (e.g. from a PATCH body or a stored
 * setting). Returns undefined when it isn't a valid mapping: exactly
 * `ACTION_BAR_HOTKEY_COUNT` entries, each null, a carried slot within
 * `MAX_CARRIED_SLOTS`, or a skill id.
 */
export function parseActionBarHotkeys(value: unknown): ActionBarHotkeyTarget[] | undefined {
    if (!Array.isArray(value) || value.length !== ACTION_BAR_HOTKEY_COUNT) {
        return undefined;
    }

    const targets: ActionBarHotkeyTarget[] = [];
    for (const entry of value) {
        const target = parseHotkeyTarget(entry);
        if (target === undefined) {
            return undefined;
        }
        targets.push(target);
    }
    return targets;
}
